import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUp, Ban } from 'lucide-react';

import { useTheme } from 'components/core/theme-provider';
import { Button } from 'components/ui/button';
import { Textarea } from 'components/ui/textarea';
import { cn } from 'lib/utils';
import { getPickerTheme } from 'pages/agents/utils/agent-config-picker-theme';
import type { ChatbotWidgetDesignSettings } from './index.type';

type PreviewChatInputProps = {
  config: ChatbotWidgetDesignSettings;
  applyWidgetColors?: boolean;
  variant?: 'default' | 'figma-edit';
  onSubmit: (value: string) => void;
  disable: boolean;
};

export function PreviewChatInput({
  config,
  applyWidgetColors = false,
  variant = 'default',
  onSubmit,
  disable,
}: PreviewChatInputProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const ui = getPickerTheme(theme === 'dark');
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [value]);

  const submitHandler = () => {
    const trimmedValue = value.trim();
    if (!trimmedValue || disable) return;

    onSubmit(trimmedValue);
    setValue('');

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const sendButtonStyle =
    applyWidgetColors && config.sendButtonBackgroundColor
      ? {
        backgroundColor: config.sendButtonBackgroundColor,
        color: config.sendButtonFontColor || undefined,
      }
      : undefined;

  const borderStyle =
    applyWidgetColors && config.borderColor
      ? { borderColor: config.borderColor }
      : undefined;

  const isFigmaEdit = variant === 'figma-edit';
  const hasPrompt = value.trim().length > 0;
  const isSendDisabled = disable || !hasPrompt;

  return (
    <form
      className={isFigmaEdit ? 'w-full shrink-0' : 'w-full border border-t-0 pb-5 pl-6 pr-6'}
      style={borderStyle}
      onSubmit={(event) => {
        event.preventDefault();
        submitHandler();
      }}
    >
      <div
        data-testid="preview-chat-input"
        className={
          isFigmaEdit
            ? cn('flex w-full items-center gap-2 rounded-2xl border px-4 py-3', !applyWidgetColors && ui.search)
            : 'mt-2 flex items-center gap-1 rounded-sm border p-2.5'
        }
        style={borderStyle}
      >
        <Textarea
          id="chatbot-widget-input"
          ref={textareaRef}
          rows={1}
          value={value}
          placeholder={
            isFigmaEdit
              ? t('HOW_CAN_I_HELP_YOU_TODAY', {
                defaultValue: 'How can I help you today?',
              })
              : t('TYPE_A_MESSAGE')
          }
          className={
            isFigmaEdit
              ? cn(
                    'h-[22px] max-h-[120px] min-h-[22px] min-w-0 flex-1 resize-none border-0 bg-transparent p-0 text-sm font-normal leading-[1.5] shadow-none placeholder:text-sm focus-visible:ring-0 focus-visible:ring-offset-0',
                    !applyWidgetColors && ui.body,
                  )
              : 'max-h-[120px] min-h-fit flex-1 resize-none border-0 bg-transparent p-0 text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0'
          }
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              submitHandler();
            }
          }}
        />

        {isFigmaEdit ? (
          <button
            type="submit"
            disabled={isSendDisabled}
            aria-label="Send message"
            className={cn(
              'flex size-8 shrink-0 items-center justify-center rounded-full disabled:pointer-events-none disabled:opacity-50',
              !applyWidgetColors && ui.pillSelected,
            )}
            style={sendButtonStyle}
          >
            {disable ? (
              <Ban className="size-4 text-error" />
            ) : (
              <ArrowUp className="size-4" />
            )}
          </button>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            type="submit"
            disabled={isSendDisabled}
            aria-label="Send message"
            className={
              applyWidgetColors
                ? 'h-8 w-8 rounded-sm p-1.5'
                : 'h-8 w-8 rounded-sm bg-secondary p-1.5'
            }
            style={sendButtonStyle}
          >
            {disable ? (
              <Ban className="aspect-square w-4 text-error" />
            ) : (
              <ArrowUp className="aspect-square w-4" />
            )}
          </Button>
        )}
      </div>
    </form>
  );
}
